import useLotteryState, { DEFAULT_LOTTERY, LotteryState } from '@/hooks/contracts/lottery/useLotteryState'
import useRoundDraw from '@/hooks/contracts/lottery/useRoundDraw'
import useChainChanged from '@/hooks/web3/useChainChanged'
import { LotteryRound } from '@/types/lottery'
import React, {
    createContext, useContext, useEffect, useState,
} from 'react'
import { usePrevious } from '@/hooks/utilities/usePrevious'
import { useSelectedGame } from './SelectedGameProvider'

type BlockPotContextType = {
    viewRoundIndex?: bigint, 
    setViewRoundIndex?: (roundIndex: bigint | undefined) => void,
    viewRoundOpened: boolean,
    setViewRoundOpened: (opened: boolean) => void,
    lottery: LotteryState
    drawnRound?: LotteryRound,
    drawnRoundOpened: boolean,
    setDrawnRoundOpened: (opened: boolean) => void,
    clearDrawnRound?: () => void,
}

const BlockPotContext = createContext<BlockPotContextType | undefined>(undefined)

type Props = {
  children: React.ReactNode
}

export default function BlockPotProvider({ children }: Props): React.ReactElement {
    const [viewRoundIndex, setViewRoundIndex] = useState<bigint | undefined>()
    const [viewRoundOpened, setViewRoundOpened] = useState(false)
    const lottery = useLotteryState()
    const chainChanged = useChainChanged()
    const { drawnRound, clearDrawnRound, drawnRoundOpened, setDrawnRoundOpened } = useRoundDraw(chainChanged)
    const { selectedGame } = useSelectedGame()
    const previousSelectedGame = usePrevious(selectedGame)

    const blockPotContextValue = {
        lottery: lottery ?? DEFAULT_LOTTERY,
        drawnRound,
        clearDrawnRound,
        drawnRoundOpened,
        setDrawnRoundOpened,
        viewRoundIndex,
        setViewRoundIndex,
        viewRoundOpened,
        setViewRoundOpened
    }

    useEffect(() => {
        if (chainChanged || (!!previousSelectedGame && previousSelectedGame != selectedGame)) {
            setViewRoundIndex(undefined)
        }
    }, [chainChanged, previousSelectedGame, selectedGame])

    return (
        <BlockPotContext.Provider value={blockPotContextValue}>
            {children}
        </BlockPotContext.Provider>
    )
}

const useBlockPot = () => {
    const context = useContext(BlockPotContext)
    if (!context) {
        throw new Error('BlockPot hooks must be used within BlockPotProvider')
    }
    return context
}

export const useLottery = () => {
    return useBlockPot().lottery
}

export const useIsDrawingNumbers = () => {
    return useBlockPot().lottery.isDrawingNumbers
}

export const useLotteryHash = () => {
    return useBlockPot().lottery.lotteryHash
}

export const useViewRoundIndex = () => {
    const { viewRoundIndex, setViewRoundIndex, viewRoundOpened, setViewRoundOpened } = useBlockPot()
    return {
        viewRoundOpened,
        setViewRoundOpened,
        viewRoundIndex,
        setViewRoundIndex,
    }
}